import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import me from "models/me";
import type { IScope } from "reducers/scope";
import { IItemRecord, IProduct } from "records/item/item";
import { IProductMedia } from "constants/product-media";
import { createProductRecord } from "helpers/product-helpers";
import makeStyles from "@material-ui/core/styles/makeStyles";
import IconButton from "@material-ui/core/IconButton";
import Grid from "@material-ui/core/Grid";
import Image360Icon from "components/icons/image-360";
import VideoPlayIcon from "components/icons/video-play";
import UploadMediaMenu from "components/product-media/upload-media-menu";
import Thumbs from "./thumbs";
import Jumbotron from "./jumbotron";
import { MEDIA_TYPE_360, MEDIA_TYPE_VIDEO } from "constants/media";
import { PANEL_SIZE, ProductMediaLightBox } from "components/product-media/media-lightbox";
import { DATATEST } from "constants/integration";

// prettier-ignore
interface IProps {
    product: IProduct;
    scope: IScope;
    imageWidth?: string | number;
    imageHeight?: string | number;
    useMagnifyZoom?: boolean;
    selectOnHover?: boolean;
    /** Show lightbox in full screen, otherwise reserve place for left panel. Default to true */
    fullWidthLightbox?: boolean;
    /** reload product data when media is uploaded */
    updateProduct?(): void;
}

export const Media: FC<IProps> = (props) => {
    const { product, scope, updateProduct } = props;
    const { useMagnifyZoom, imageHeight, imageWidth, selectOnHover, fullWidthLightbox = true } = props;
    const [selectedIndex, setSelectedIndex] = useState<number>(0);
    const [lightBoxOpen, setLightBoxOpen] = useState<boolean>(false);
    const useUnlimitedImages = useMemo(() => scope.brand.hasPreference("unlimited_images"), [scope]);
    const canShowUploadMediaOption = scope.brand.hasPreference("use_new_permissions")
        ? me.hasPermission("base_edit_product")
        : me.hasPermission("update_brand");

    // Reset if new item is loaded
    useEffect(() => setSelectedIndex(0), [product]);

    const record = useMemo<IItemRecord>(() => createProductRecord(product), [product]);
    const media = useMemo<IProductMedia[]>(() => record.getMedia(scope), [record]); // eslint-disable-line react-hooks/exhaustive-deps
    const index360 = useMemo(() => _.findIndex(media, { type: MEDIA_TYPE_360 }), [media]);
    const indexVideo = useMemo(() => _.findIndex(media, { type: MEDIA_TYPE_VIDEO }), [media]);

    const handleSelect = useCallback(
        (index: number) => () => {
            setSelectedIndex(index);
            setLightBoxOpen(true);
        },
        []
    );

    const closeLightBox = useCallback(() => setLightBoxOpen(false), []);

    const classes = useStyles({});
    return (
        <section>
            <div data-test={DATATEST.GALLERY.ITEM_PREVIEW_IMAGE} className={classes.view}>
                <Thumbs
                    product={record}
                    selectedIndex={selectedIndex}
                    onChange={setSelectedIndex}
                    scope={scope}
                    selectOnHover={selectOnHover}
                />
                <Jumbotron
                    product={record}
                    useMagnifyZoom={useMagnifyZoom}
                    selectedIndex={selectedIndex}
                    onChangeMediaIndex={setSelectedIndex}
                    fullWidthLightbox={fullWidthLightbox}
                    width={imageWidth}
                    height={imageHeight}
                    useCarousel={useUnlimitedImages}
                />
            </div>
            <Grid container={true} justify={useUnlimitedImages ? "flex-end" : "flex-start"}>
                {!useUnlimitedImages && (
                    <Grid item={true} container={true} xs={true}>
                        {index360 > 0 && (
                            <Grid className={classes.grid} item={true}>
                                <IconButton className={classes.button} onClick={handleSelect(index360)}>
                                    <Image360Icon className={classes.icon} data-test={DATATEST.PDP.THUMBS_360} />
                                </IconButton>
                            </Grid>
                        )}
                        {indexVideo > 0 && (
                            <Grid className={classes.grid} item={true}>
                                <IconButton className={classes.button} onClick={handleSelect(indexVideo)}>
                                    <VideoPlayIcon className={classes.icon} data-test={DATATEST.PDP.THUMBS_VIDEO} />
                                </IconButton>
                            </Grid>
                        )}
                    </Grid>
                )}
                {canShowUploadMediaOption && (
                    <Grid className={classes.grid} item={true}>
                        <UploadMediaMenu
                            scope={scope}
                            product={record}
                            updateProduct={updateProduct}
                            classes={{ button: classes.uploadButton, icon: classes.icon }}
                        />
                    </Grid>
                )}
            </Grid>

            {lightBoxOpen && ( // optimization not to load media
                <ProductMediaLightBox
                    open={lightBoxOpen}
                    product={product}
                    currentIndex={selectedIndex}
                    onClose={closeLightBox}
                    onViewChange={setSelectedIndex}
                    panelWidth={fullWidthLightbox ? 0 : PANEL_SIZE}
                    scope={scope}
                />
            )}
        </section>
    );
};

const useStyles = makeStyles({
    view: {
        display: "flex",
    },
    grid: {
        padding: 6,
    },
    button: {
        width: 36,
        height: 36,
        padding: 0,
        backgroundColor: "rgba(245,245,245,0.7)",
    },
    icon: {
        width: 36,
        height: 36,
    },
    uploadButton: {
        width: 36,
        height: 36,
        padding: 0,
        backgroundColor: "inherit",
    },
});
